import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

import { getSyncEngine } from './engine';

const LOCAL_SAVE_SUPPRESS_MS = 5_000;

export type PageEditorHandle = {
  isDirty: () => boolean;
  reload: () => void;
};

type WebSyncContextValue = {
  registerPageEditor: (pageId: string, handle: PageEditorHandle) => () => void;
  notifyLocalSave: (pageId: string) => void;
};

const WebSyncContext = createContext<WebSyncContextValue | null>(null);

export function WebSyncProvider({ children }: { children: ReactNode }) {
  const editorsRef = useRef(new Map<string, PageEditorHandle>());
  const localSaveSuppressUntilRef = useRef(new Map<string, number>());

  const registerPageEditor = useCallback((pageId: string, handle: PageEditorHandle) => {
    editorsRef.current.set(pageId, handle);
    return () => {
      editorsRef.current.delete(pageId);
      localSaveSuppressUntilRef.current.delete(pageId);
    };
  }, []);

  const notifyLocalSave = useCallback((pageId: string) => {
    localSaveSuppressUntilRef.current.set(pageId, Date.now() + LOCAL_SAVE_SUPPRESS_MS);
  }, []);

  useEffect(() => {
    // Reuse the sync engine's single WebSocket (applies deltas + notifyRemote).
    // A second connection via RemoteChangeListener caused reconnect races and
    // "closed before the connection is established" console noise.
    const engine = getSyncEngine();
    return engine.subscribeRemote(() => {
      const now = Date.now();
      for (const [pageId, handle] of editorsRef.current) {
        const suppressUntil = localSaveSuppressUntilRef.current.get(pageId) ?? 0;
        if (now < suppressUntil) {
          continue;
        }
        if (handle.isDirty()) {
          continue;
        }
        handle.reload();
      }
    });
  }, []);

  const value = useMemo(
    () => ({
      registerPageEditor,
      notifyLocalSave,
    }),
    [registerPageEditor, notifyLocalSave],
  );

  return <WebSyncContext.Provider value={value}>{children}</WebSyncContext.Provider>;
}

export function useWebSync(): WebSyncContextValue {
  const ctx = useContext(WebSyncContext);
  if (!ctx) {
    throw new Error('useWebSync must be used within WebSyncProvider');
  }
  return ctx;
}

export function useWebSyncPage(pageId: string): {
  registerEditor: (handle: PageEditorHandle) => () => void;
  notifyLocalSave: () => void;
} {
  const { registerPageEditor, notifyLocalSave } = useWebSync();

  const registerEditor = useCallback(
    (handle: PageEditorHandle) => registerPageEditor(pageId, handle),
    [pageId, registerPageEditor],
  );

  const notifyPageLocalSave = useCallback(
    () => notifyLocalSave(pageId),
    [pageId, notifyLocalSave],
  );

  return { registerEditor, notifyLocalSave: notifyPageLocalSave };
}
